"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { CircleQuestionMark as HelpCircle, MessageSquare, Phone, Mail, CircleCheck as CheckCircle2, ArrowRight, Search, Sparkles, MapPin, Clock, UserCheck } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const faqData = [{
  id: "faq-1",
  question: "Why is Local SEO crucial for businesses in Wakefield and West Yorkshire?",
  answer: "Wakefield is a rapidly growing economic hub in West Yorkshire. Local SEO ensures your business appears directly in Google's Map Pack and local organic results when prospective clients in Wakefield, Pontefract, Castleford, Ossett, and surrounding areas search for your services. I optimize your presence so high-intent local traffic converts into direct enquiries for your business."
}, {
  id: "faq-2",
  question: "Will I work directly with you, Yassine, or an account manager?",
  answer: "You work directly with me—Yassine Essaadi. Unlike traditional SEO agencies that hand your campaign off to junior account managers or offshore teams, I handle your strategy, technical audit, content optimization, and local citation work personally. You get transparent, expert advice with no middleman layer."
}, {
  id: "faq-3",
  question: "How quickly can my Wakefield business expect to see Local SEO results?",
  answer: "Initial quick wins—such as Google Business Profile fixes, NAP (Name, Address, Phone) consistency corrections, and primary local page optimizations—typically show movement within 4 to 8 weeks. Competing for competitive local keywords in Wakefield usually yields strong, sustained rankings and inbound lead increases within 3 to 6 months."
}, {
  id: "faq-4",
  question: "How do you optimize my Google Business Profile (GBP) for local searches?",
  answer: "I execute a detailed GBP overhaul: setting accurate primary and secondary categories, optimizing business descriptions with geographic relevance, configuring local service areas, structuring geotagged visual assets, and setting up structured Q&A. I also implement a strategy for acquiring high-value, authentic customer reviews."
}, {
  id: "faq-5",
  question: "Do I need Local SEO if my business serves locations beyond Wakefield?",
  answer: "Yes, absolutely. Local SEO is foundational even for multi-location or regional enterprises. By creating tailored location pages, managing local schema markup, and establishing regional authority, I help you rank in Wakefield while expanding your organic search footprint across Leeds, Bradford, and nationwide markets."
}, {
  id: "faq-6",
  question: "How does Local SEO differ from national or technical SEO?",
  answer: "While national SEO focuses on broad keyphrases without location intent, Local SEO targets geo-specific queries like 'SEO consultant Wakefield' or 'commercial solicitor near me'. It relies heavily on Google Business Profile, localized backlinks, geographic citations, map pack visibility, and local structured data to capture nearby searchers."
}, {
  id: "faq-7",
  question: "How do you handle local citations and Wakefield business directories?",
  answer: "I manually audit, build, and clean up your citations across primary UK directories and Yorkshire-specific business registries. Ensuring your business Name, Address, and Phone number (NAP) are 100% consistent across every platform builds vital location trust signals for Google."
}, {
  id: "faq-8",
  question: "What is included in your local SEO audit for Wakefield companies?",
  answer: "My local SEO audit covers a comprehensive technical health check, Google Business Profile evaluation, local keyword gap analysis, competitor profiling in the Wakefield market, citation consistency review, and localized link profile analysis. You receive a clear, actionable roadmap with clear priorities."
}, {
  id: "faq-9",
  question: "How do you track and report on local keyword rankings and ROI?",
  answer: "I provide transparent, easy-to-understand monthly reports measuring local map rankings, organic keyword positions, website visits, phone calls, form submissions, and conversion trends. No vanity metrics—just real business metrics that track growth and revenue impact."
}, {
  id: "faq-10",
  question: "How much does a tailored Local SEO strategy for a Wakefield business cost?",
  answer: "Every strategy is custom-built around your industry's competitiveness, your current search presence, and your growth goals. I work on transparent monthly retainers or fixed-scope project agreements without locking you into predatory long-term contracts."
}, {
  id: "faq-11",
  question: "Can Local SEO help e-commerce or B2B businesses operating in Wakefield?",
  answer: "Yes. For B2B businesses, local authority establishes immediate regional trust when decision-makers search for local partners. For e-commerce brands with physical premises or regional click-and-collect services, local SEO captures high-converting hyper-local shoppers."
}, {
  id: "faq-12",
  question: "How do we get started with your Wakefield local search optimization?",
  answer: "The first step is booking a free, no-obligation SEO consultation with me. I will review your current website and local visibility, discuss your business objectives, and explain how we can achieve sustainable search growth together."
}];
const highlights = [{
  id: "hl-1",
  title: "100% Direct Access",
  desc: "No account managers or sales reps. You consult directly with Yassine."
}, {
  id: "hl-2",
  title: "Tailored to Wakefield",
  desc: "Hyper-focused strategies built specifically for Yorkshire market dynamics."
}, {
  id: "hl-3",
  title: "Transparent Metrics",
  desc: "Clear reporting focused on leads, phone calls, and revenue impact."
}];
export default function Faq() {
  const [searchQuery, setSearchQuery] = useState("");
  const filteredFaqs = faqData.filter(item => item.question.toLowerCase().includes(searchQuery.toLowerCase()) || item.answer.toLowerCase().includes(searchQuery.toLowerCase()));
  return <section id="localseowakefieldfaq" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="absolute top-0 right-1/4 -z-10 size-96 rounded-full bg-secondary/30 blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 left-10 -z-10 size-80 rounded-full bg-primary/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 text-xs font-semibold tracking-wider uppercase border border-border">
            <MapPin className="size-3.5 mr-1 text-primary" />
            Wakefield Local SEO FAQs
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-foreground tracking-tight mb-4">
            Everything You Need to Know About Winning <span className="text-primary">Wakefield Search</span>
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Direct, transparent answers from Yassine Essaadi on how bespoke local search optimization helps Wakefield and West Yorkshire businesses outrank local competitors and generate high-value leads.
          </p>

          <div className="mt-8 relative max-w-md mx-auto">
            <div className="relative flex items-center">
              <Search className="absolute left-3.5 size-4 text-muted-foreground pointer-events-none" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search Wakefield Local SEO questions..." className="w-full pl-10 pr-4 py-2.5 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-[border-color,box-shadow]" />
            </div>
            {searchQuery && <p className="text-xs text-muted-foreground mt-2 text-left px-1">
                Showing {filteredFaqs.length} of {faqData.length} questions
              </p>}
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {highlights.map((hl, i) => <motion.div key={hl.id} data-index={i} initial={{
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4,
          delay: i * 0.1
        }} className="bg-card border border-border rounded-xl p-5 flex items-start space-x-4 shadow-xs">
              <div className="p-2.5 rounded-lg bg-secondary/80 text-primary shrink-0">
                {i === 0 && <UserCheck className="size-5" />}
                {i === 1 && <MapPin className="size-5" />}
                {i === 2 && <CheckCircle2 className="size-5" />}
              </div>
              <div>
                <h3 className="text-base font-serif font-semibold text-foreground mb-1">
                  {hl.title}
                </h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {hl.desc}
                </p>
              </div>
            </motion.div>)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-sm">
              {filteredFaqs.length === 0 ? <div className="py-12 text-center">
                  <HelpCircle className="size-10 text-muted-foreground mx-auto mb-3 opacity-60" />
                  <p className="text-base font-semibold text-foreground mb-1">No matching questions found</p>
                  <p className="text-sm text-muted-foreground mb-6">
                    {"Try searching for terms like 'Google Business', 'audit', 'results', or 'pricing'."}
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setSearchQuery("")}>
                    Reset Search
                  </Button>
                </div> : <Accordion type="single" collapsible className="w-full space-y-4">
                  {filteredFaqs.map((faq, i) => <AccordionItem key={faq.id} data-index={i} value={faq.id} className="border border-border/80 rounded-xl px-4 sm:px-5 py-1 bg-background hover:border-primary/40 transition-[border-color,box-shadow]">
                      <AccordionTrigger className="text-left font-serif font-semibold text-base sm:text-lg text-primary hover:no-underline py-4">
                        <span className="flex items-center gap-3">
                          <span className="text-xs font-sans text-muted-foreground bg-muted px-2 py-0.5 rounded-md font-medium shrink-0">
                            {i + 1 < 10 ? `0${i + 1}` : i + 1}
                          </span>
                          <span>{faq.question}</span>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1 pb-5 pl-9 pr-2">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>}
            </motion.div>
          </div>

          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-card border border-border rounded-2xl p-6 sm:p-8 shadow-md relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-secondary/50 rounded-bl-full -z-0 pointer-events-none" />

              <div className="relative z-10">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="size-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <Sparkles className="size-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-serif font-bold text-foreground">
                      Have a specific Wakefield SEO question?
                    </h3>
                    <p className="text-xs text-muted-foreground">Work directly with Yassine Essaadi</p>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                  Every local business is different. Book a free 30-minute local SEO audit & strategy consultation to discuss your specific goals in Wakefield and West Yorkshire.
                </p>

                <div className="space-y-3 mb-6 text-xs text-foreground">
                  <div className="flex items-center space-x-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Direct consultant access (No sales pitch)</span>
                  </div>
                  <div className="flex items-center space-x-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Free Wakefield competitor breakdown</span>
                  </div>
                  <div className="flex items-center space-x-2.5">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>Actionable roadmap with clear deliverables</span>
                  </div>
                </div>

                <Separator className="my-6" />

                <div className="space-y-3 mb-6">
                  <Button asChild variant="default" size="lg" className="w-full justify-center group font-medium">
                    <Link to="/ContactPage">
                      <span>Book Free SEO Consultation</span>
                      <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>

                  <Button asChild variant="outline" size="default" className="w-full justify-center">
                    <Link to="/CaseStudiesPage">
                      View Verified Case Studies
                    </Link>
                  </Button>
                </div>

                <div className="pt-2 border-t border-border/60 space-y-2">
                  <a href="mailto:contact@ye-consultancy.com" className="flex items-center text-xs text-muted-foreground hover:text-primary transition-colors">
                    <Mail className="size-3.5 mr-2 text-primary shrink-0" />
                    <span>contact@ye-consultancy.com</span>
                  </a>
                  <a href="tel:+447490984430" className="flex items-center text-xs text-muted-foreground hover:text-primary transition-colors">
                    <Phone className="size-3.5 mr-2 text-primary shrink-0" />
                    <span>+44 7490984430</span>
                  </a>
                  <div className="flex items-center text-xs text-muted-foreground">
                    <Clock className="size-3.5 mr-2 text-primary shrink-0" />
                    <span>Windmill Hill Business Park, Swindon SN5 6QR</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <div className="mt-4 p-4 rounded-xl bg-secondary/50 border border-border text-center">
              <p className="text-xs text-muted-foreground font-medium">
                Independent Freelance SEO Consultancy — Delivering Sustainable Search Growth Across the UK
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}